import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { flatMap, map, take } from 'rxjs/operators';

import { IEditSeasonValues, ISeason, ISeasonRaw } from './season.interface';
import { ApiResourceService } from '../api-resource/api-resource.service';
import { AuthenticationService } from '../authentication/authentication.service';

@Injectable()
export class SeasonResourceService {
  private http = inject(HttpClient);
  private readonly apiResourceService = inject(ApiResourceService);
  private readonly authService = inject(AuthenticationService);

  public list(params: {
    is_open?: boolean;
    is_open_for_admin?: boolean;
  }): Observable<ISeason[]> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) => {
        return this.http.get<ISeasonRaw[]>(
          this.apiResourceService.getApiUrl(`/seasons`),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
            params: {
              ...(params.is_open !== undefined
                ? { is_open: `${params.is_open}` }
                : {}),
              ...(params.is_open_for_admin !== undefined
                ? { is_open_for_admin: `${params.is_open_for_admin}` }
                : {}),
            },
          },
        );
      }),
      map((seasons: ISeasonRaw[]): ISeason[] => (seasons || []).map(mapSeason)),
    );
  }

  public get(id: number): Observable<ISeason> {
    return this.http
      .get<ISeasonRaw>(
        this.apiResourceService.getApiUrl(`/seasons/${encodeURIComponent(id)}`),
      )
      .pipe(map(mapSeason));
  }

  public edit(id: number, season: IEditSeasonValues): Observable<ISeason> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) =>
        this.http.put<ISeasonRaw>(
          this.apiResourceService.getApiUrl(`/seasons/${id}`),
          serializeSeasonForApi(season),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        ),
      ),
      map(mapSeason),
    );
  }

  public create(season: IEditSeasonValues): Observable<ISeason> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) =>
        this.http.put<ISeasonRaw>(
          this.apiResourceService.getApiUrl(`/seasons`),
          serializeSeasonForApi(season),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        ),
      ),
      map(mapSeason),
    );
  }

  public destroy(id: number): Observable<void> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) =>
        this.http.delete<void>(
          this.apiResourceService.getApiUrl(`/seasons/${id}`),
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        ),
      ),
    );
  }

  public anonymize(id: number): Observable<ISeason> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) =>
        this.http.post<ISeasonRaw>(
          this.apiResourceService.getApiUrl(`/seasons/${id}/anonymize`),
          {},
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        ),
      ),
      map(mapSeason),
    );
  }

  public duplicateConfigFrom(
    targetSeasonId: number,
    sourceSeasonId: number,
  ): Observable<ISeasonConfigDuplicationSummary> {
    return this.authService.getToken().pipe(
      take(1),
      flatMap((token) =>
        this.http.post<ISeasonConfigDuplicationSummary>(
          this.apiResourceService.getApiUrl(
            `/seasons/${encodeURIComponent(targetSeasonId)}/duplicate-config-from/${encodeURIComponent(sourceSeasonId)}`,
          ),
          {},
          {
            headers: this.apiResourceService.getAuthorizationHeader(token),
          },
        ),
      ),
    );
  }
}

export interface ISeasonConfigDuplicationSummary {
  pricing: number;
  tarif_categories: number;
  discounts: number;
  consent_clauses: number;
}

function mapSeason(raw: ISeasonRaw): ISeason {
  return {
    ...raw,
    presubscription_closing_date: mapDate(raw.presubscription_closing_date),
    presubscription_opening_date: mapDate(raw.presubscription_opening_date),
    presubscription_cancel_only_date: mapDate(
      raw.presubscription_cancel_only_date,
    ),
    presubscription_stop_admin_date: mapDate(
      raw.presubscription_stop_admin_date,
    ),
    accounting_closing_date: mapDate(raw.accounting_closing_date),
  };
}

/**
 * Serialize season data for API, converting Date objects to appropriate formats.
 * - Opening date: ISO 8601 with exact time (user-specified hour)
 * - Closing/deadline dates (DATETIME): ISO 8601 with time 23:59:59 (end of day)
 * - Accounting closing date (DATE): YYYY-MM-DD format (date only, no time)
 *
 * All date fields must be explicitly included (even as null) because the
 * backend validation uses 'present' rule which requires the key to exist.
 */
function serializeSeasonForApi(
  season: IEditSeasonValues,
): Record<string, string | null> {
  return {
    name: season.name,
    short_name: season.short_name ?? null,
    presubscription_opening_date:
      formatDateTimeForApi(season.presubscription_opening_date) ?? null,
    presubscription_closing_date:
      formatDateEndOfDayForApi(season.presubscription_closing_date) ?? null,
    presubscription_cancel_only_date:
      formatDateEndOfDayForApi(season.presubscription_cancel_only_date) ?? null,
    accounting_closing_date:
      formatDateOnlyForApi(season.accounting_closing_date) ?? null,
    presubscription_stop_admin_date:
      formatDateEndOfDayForApi(season.presubscription_stop_admin_date) ?? null,
    subscription_date: season.subscription_date ?? null,
    subscription_place: season.subscription_place ?? null,
  };
}

/**
 * Format a date as ISO 8601 with time set to 23:59:59 local time.
 * Used for closing/deadline dates (DATETIME columns) where we want the entire day to be valid.
 */
function formatDateEndOfDayForApi(date: Date | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  // Create a new date at 23:59:59 local time
  const endOfDay = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    23,
    59,
    59,
  );
  return endOfDay.toISOString();
}

/**
 * Format a date as YYYY-MM-DD (date only, no time).
 * Used for DATE columns in the database (not DATETIME).
 */
function formatDateOnlyForApi(date: Date | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

function formatDateTimeForApi(date: Date | undefined): string | undefined {
  if (!date) {
    return undefined;
  }
  // Send datetime in UTC format (ISO 8601)
  // JavaScript's toISOString() returns UTC time with Z suffix
  return date.toISOString();
}

function mapDate(value: string | null | undefined): Date | undefined {
  if (!value) {
    return undefined;
  }

  // Handle date-only strings (YYYY-MM-DD) to avoid timezone issues
  // new Date("2026-01-01") parses as UTC midnight, causing day shift in local timezone
  // We parse manually to create a local midnight date
  const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnlyMatch) {
    const [, year, month, day] = dateOnlyMatch;
    return new Date(
      parseInt(year, 10),
      parseInt(month, 10) - 1,
      parseInt(day, 10),
    );
  }

  // For datetime strings (ISO 8601 with Z suffix), use standard parsing
  // JavaScript correctly interprets the Z suffix as UTC and converts to local time
  return new Date(value);
}
